import React from 'react';
import { useReadAloud } from '../../contexts/ReadAloudContext';
import './ReadAloudButton.css';

/**
 * ReadAloudButton - A button component for initiating the read-aloud functionality
 * Following best practices:
 * - Pure UI component that only triggers actions
 * - No data passing - uses context for data flow
 * - Simple props for UI customization only
 * 
 * @param {Object} props - Component props 
 * @param {boolean} props.disabled - Whether the button is disabled
 * @param {string} props.className - Additional CSS class names
 * @param {boolean} props.iconOnly - Whether to show only the icon
 * @param {number} props.pageIndex - Optional specific page to start reading from
 * @returns {JSX.Element} Button component
 */
const ReadAloudButton = ({ 
  disabled = false,
  className = '',
  iconOnly = false,
  pageIndex = null
}) => {
  // Use the high-level startReadingCurrentStory method that handles all orchestration
  const { startReadingCurrentStory, isPlaying, readAloudStoryId } = useReadAloud();
  
  // Only show in development and test environments
  if (process.env.NODE_ENV !== 'development' && process.env.NODE_ENV !== 'test') return null;
  
  // Hide if already playing
  if (isPlaying) return null;
  
  const baseClass = 'read-aloud-button';
  const iconOnlyClass = iconOnly ? `${baseClass}--icon-only` : '';
  const combinedClassName = `${baseClass} ${iconOnlyClass} ${className}`.trim();
  
  // Always use "Read Aloud" as the text for non-icon buttons
  const buttonText = iconOnly ? '' : 'Read Aloud';
  
  // Simple handler with no orchestration - all complexity moved to the context
  const handleClick = () => {
    if (!disabled) {
      console.log('[READALOUD-DEBUG] ReadAloudButton clicked', {
        pageIndex,
        isPlaying,
        currentReadAloudStoryId: readAloudStoryId
      });
      
      // This method handles all the orchestration including loading the story
      startReadingCurrentStory(pageIndex);
    }
  };
  
  return (
    <button 
      className={combinedClassName}
      onClick={handleClick}
      disabled={disabled}
      aria-label="Read story aloud"
      title="Read story aloud"
      data-testid="read-aloud-button"
    >
      <span className={`${baseClass}__icon`} aria-hidden="true"></span>
      {!iconOnly && <span className={`${baseClass}__text`}>{buttonText}</span>}
    </button>
  );
};

export default ReadAloudButton;